import React from "react";
import { IMember } from "../../types/interfaces/IMember";
import { CardContent, Box, Typography } from "@mui/material";
import { AapColors } from '../../../global/styles/AapColors';
import { DirectoryResource } from '../../constants/DirectoryResource';

/**
 * EducationAndTrainingCard Component
 * 
 * This component creates a card for displaying the education and training information of a member.
 * It displays the board certifications, specialties, medical school graduation year, languages, and gender of the member.
 * 
 * Props:
 * - member: An object of type IMember containing the member's information.
 * 
 * Example usage:
 * ```tsx
 * <EducationAndTrainingCard member={member} />
 * ```
 * 
 * @param {IMember} member - The member object containing the education and training information.
 * @returns {JSX.Element} A card displaying the member's education and training information.
 */
export default function EducationAndTrainingCard({ member }: { member: IMember }) {
    return (<>
        {(member.Board || member.Specialties || member.SubSpecialties || member.Interests || member.MedicalSchoolGraduateYear || member.Languages || member.Gender) && (
            <React.Fragment>
                <CardContent>
                    <Typography sx={{color: AapColors.AapBrightIndigo, fontSize: "1em"}}>{DirectoryResource.education_lbl}</Typography>
                    <Box sx={{ flexDirection: 'row' }}>
                        {member.Board && (
                            <Box>
                                <Typography sx={{ fontSize: "0.8em", fontWeight: "bold" }}>{DirectoryResource.boards_lbl}:</Typography>
                                {member.Board &&
                                    member.Board.split(',').map((item, index) => (
                                        <Typography sx={{ fontSize: "0.8em", margin: "0, auto" }} key={index}>{item.trim()}</Typography>
                                    ))}

                            </Box>)}
                        {member.Specialties && (
                            <Box>
                                <Typography sx={{ fontSize: "0.8em", fontWeight: "bold" }}>{DirectoryResource.specialty_lbl}:</Typography>
                                {member.Specialties &&
                                    member.Specialties.split(',').map((item, index) => (
                                        <Typography sx={{ fontSize: "0.8em", margin: "0, auto" }} key={index}>{item.trim()}</Typography>
                                    ))}
                            </Box>)}
                        {member.SubSpecialties && (
                            <Box>
                                <Typography sx={{ fontSize: "0.8em", fontWeight: "bold" }}>{DirectoryResource.sub_specialty_lbl}:</Typography>
                                {member.SubSpecialties &&
                                    member.SubSpecialties.split(',').map((item, index) => (
                                        <Typography sx={{ fontSize: "0.8em", margin: "0, auto" }} key={index}>{item.trim()}</Typography>
                                    ))}
                            </Box>)}
                        {member.Interests && (
                            <Box>
                                <Typography sx={{ fontSize: "0.8em", fontWeight: "bold" }}>{DirectoryResource.interest_lbl}:</Typography>
                                {member.Interests &&
                                    member.Interests.split(',').map((item, index) => (
                                        <Typography sx={{ fontSize: "0.8em", margin: "0, auto" }} key={index}>{item.trim()}</Typography>
                                    ))}
                            </Box>)}
                        {member.MedicalSchoolGraduateYear && (
                            <Box>
                                <Typography sx={{ fontSize: "0.8em", fontWeight: "bold" }}>{DirectoryResource.gradYear_lbl}:</Typography>
                                <Typography sx={{ fontSize: "0.8em", margin: "0, auto" }}>{member.MedicalSchoolGraduateYear}</Typography>
                            </Box>)}
                        {member.Languages && (
                            <Box>
                                <Typography sx={{ fontSize: "0.8em", fontWeight: "bold" }}>{DirectoryResource.languages_lbl}:</Typography>
                                <Typography sx={{ fontSize: "0.8em", margin: "0, auto" }}>{member.Languages}</Typography>
                            </Box>)}
                        {member.Gender && (
                            <Box>
                                <Typography sx={{ fontSize: "0.8em", fontWeight: "bold" }}>{DirectoryResource.gender_lbl}:</Typography>
                                <Typography sx={{ fontSize: "0.8em", margin: "0, auto" }}>{member.Gender}</Typography>
                            </Box>)}
                    </Box>
                </CardContent>

            </React.Fragment>
        )}
    </>
    );
}